import { Injectable } from '@angular/core';
import { TypeFormats } from 'src/shared/types/exportItem';
import { CVApiService } from '../../reusables/services/cv-api.service';
import {
    GroupAttributes, GroupDTO, GroupTypeDTO, ImageDTO, ImgUpdateResponse, LDGroupInfo, LoginWTO, PermissionEmpGroups,
    UpdatePermissionGroupDTO, PermissionRecGroups, Permissions, ProfileDTO, User, UserAdminServiceData, UserProfileDTO,
    SalesPackageAssignmentWSTO, GemHistoryDTOV2, SpendingPointHistoryDTOV2, AttributeCategoryDTO, Language, UpdateUserDTO,
    DashboardDTO,UserFilter, pagePackage, PageDTO, AddEditPageAccessByLoginParameters, pageAccessForUser, GetModuleAccessByLoginIDParameters, DashboardModuleAccess, AddEditModuleAccessByLoginParameters, SalesPackage
} from './user-admin.model';
import { Subject } from 'rxjs';

@Injectable({
    providedIn: 'root'
})
export class UserAdminService {
    public dataObj: UserAdminServiceData = {
        orderBy: '',
        selectedUserName: '',
        permissions: null
    };

    // Subject to allow user-admin, user-list and notifications (TODO) to communicate
    userPictureApprovedDenied = new Subject<UserProfileDTO>();

    constructor(
        private apiService: CVApiService
    ) {
    }

    // gets the login user's permissions to view users' attributes and permissions
    async getLoginUserPermissions(): Promise<Permissions> {
        let response = await this.apiService.get<Permissions>("/api/v1/getLoginUserPermissions/");
        if (response.status_code < 200 || response.status_code >= 300) {
            console.error("Failed to get permissions.");
            throw response;
        }
        this.dataObj.permissions = response.data;
        return response.data;
    }

    //gets list of user logins to be displayed on UserAdmin landing
    async loadUsersList(): Promise<User[]> {
        let response = await this.apiService.get<LoginWTO[]>("/api/v1/users/");
        if (response.status_code < 200 || response.status_code >= 300) {
            console.error("Failed to get users.");
            throw response;
        }
        return response.data;
    };

    //gets users export file
    async exportUsers(format: TypeFormats, filters: UserFilter): Promise<string> {
        const url = `/api/v1/users/export?format=${format}`;
        let response = await this.apiService.post<string>(url, JSON.stringify(filters));

        if (response.status_code < 200 || response.status_code >= 300) {
            console.error("Failed to get file.");
            throw response;
        }
        return response.data;
    }

    //gets list of user profiles for landing page
    async loadUsersProfilesList(): Promise<UserProfileDTO[]> {
        let response = await this.apiService.get<UserProfileDTO[]>("/api/v1/usersProfiles/");
        if (response.status_code < 200 || response.status_code >= 300) {
            console.error("Failed to get user profiles.");
            throw response;
        }
        return response.data;
    };

    //gets list of all base group types for details page
    async loadBaseGroupTypes(): Promise<string[]> {
        let response = await this.apiService.get<string[]>("/api/v1/GetBaseGroupTypes/");
        if (response.status_code < 200 || response.status_code >= 300) {
            console.error("Failed to get base group types.");
            throw response;
        }
        return response.data;
    };

    //gets list of user profile pictures for landing page
    async loadUsersProfilesPicturesList(): Promise<ImageDTO[]> {
        let response = await this.apiService.get<ImageDTO[]>("/api/v1/usersProfilesPictures/");
        if (response.status_code < 200 || response.status_code >= 300) {
            console.error("Failed to get user profile pictures.");
            throw response;
        }
        return response.data;
    };

    //gets info for specific passed in loginID
    async getUserInfo(loginID: number): Promise<LoginWTO> {
        let response = await this.apiService.get<LoginWTO>("/api/v1/userInfo/" + loginID);
        if (response.status_code < 200 || response.status_code >= 300) {
            console.error("Failed to get user info by loginID.");
            throw response;
        }
        return response.data;
    };

    //gets full name for specific passed in loginID
    async getUserFullName(loginID: number): Promise<string> {
        let response = await this.apiService.get<string>("/api/v1/GetFullName/" + loginID);
        if (response.status_code < 200 || response.status_code >= 300) {
            console.error("Failed to get user full name by loginID.");
            throw response;
        }
        return response.data;
    };

    /// Returns user's ProfileDTO based on LoginID
    async getUserProfile(loginID: number): Promise<UserProfileDTO> {
        let response = await this.apiService.get<UserProfileDTO>("/api/v1/userProfile/" + loginID);
        if (response.status_code < 200 || response.status_code >= 300) {
            console.error("Failed to get user profileDTO by loginID.");
            throw response;
        }
        return response.data;
    };

    //returns the ImageDTO for the passed imageID
    async getUserPicture(imageID: number): Promise<ImageDTO> {
        let response = await this.apiService.get<ImageDTO>("/api/v1/userPicture/" + imageID);
        if (response.status_code < 200 || response.status_code >= 300) {
            console.error("Failed to get user picture by imageID.");
            throw response;
        }
        return response.data;
    };

    //returns the Agent Attributes for the selected user
    async getAgentAttributesByGroup(groupID: number): Promise<GroupAttributes> {
        let response = await this.apiService.get<GroupAttributes>("/api/v1/GetAgentAttributesByGroup/" + groupID);
        if (response.status_code < 200 || response.status_code >= 300) {
            console.error("Failed to get user agent attributes by group.");
            throw response;
        }
        return response.data;
    };

    //impersonate user
    async impersonateUser(loginID: number): Promise<void> {
        let response = await this.apiService.get<void>("/api/v1/ImpersonateUser/" + loginID);
        if (response.status_code < 200 || response.status_code >= 300) {
            console.error("Failed to impersonate user.");
            throw response;
        }
        return response.data;
    };

    //returns a list of all profileDTOs for logged in company
    async getAllProfileDTOs(): Promise<ProfileDTO[]> {
        let response = await this.apiService.get<ProfileDTO[]>("/api/v1/GetAllProfileDTOs/");
        if (response.status_code < 200 || response.status_code >= 300) {
            console.error("Failed to get all profileDTOs.");
            throw response;
        }
        return response.data;
    };

    async getAllLogins() {
        let response = await this.apiService.get("/api/v1/getAllLogins");
        if (response.status_code < 200 || response.status_code >= 300) {
            throw response;
        }
        return response.data;
    };

    async switchUserProfileActive(loginID: number, active: boolean): Promise<boolean> {
        let response = await this.apiService.post<boolean>("/api/v1/updateuserprofileactive/",
            JSON.stringify({ loginID: loginID, active: active }));
        if (response.status_code < 200 || response.status_code >= 300) {
            console.error("Failed to switch users active status.");
            throw response;
        }
        return response.data;
    };

    // Update User Profile Image
    async updateUserImage(loginID: number, imgStr: string): Promise<ImgUpdateResponse> {
        let response = await this.apiService.post<ImgUpdateResponse>("/api/v1/UpdateUserImage/" + loginID, JSON.stringify(imgStr));
        if (response.status_code < 200 || response.status_code >= 300) {
            console.error("Failed to update user image.");
            throw response;
        }
        return response.data;
    };

    //gets list of user EmpGroupSec info for permission page
    async loadUserEmpGroupInfo(loginID: number): Promise<PermissionEmpGroups[][]> {
        let response = await this.apiService.get<PermissionEmpGroups[][]>("/api/v1/userEmpGroupInfo/" + loginID);
        if (response.status_code < 200 || response.status_code >= 300) {
            console.error("Failed to get user emp group info by loginID.");
            throw response;
        }
        return response.data;
    };

    //gets list of user RecGroupSec info for permission page
    async loadUserRecGroupInfo(loginID: number): Promise<PermissionRecGroups[][]> {
        let response = await this.apiService.get<PermissionRecGroups[][]>("/api/v1/userRecGroupInfo/" + loginID);
        if (response.status_code < 200 || response.status_code >= 300) {
            console.error("Failed to get reg group info by loginID.");
            throw response;
        }
        return response.data;
    };

    async GetAllLDGroupInfo(baseGroupType: number, groupLevel: number, groupTypeName: string): Promise<LDGroupInfo> {
        let response = await this.apiService.get<GroupDTO[]>("/api/v1/GetAllLDGroupInfo/" + baseGroupType + "/" + groupLevel);
        if (response.status_code < 200 || response.status_code >= 300) {
            console.error("Failed to get all ld group info.");
            throw response;
        }
        return {
            Key: groupTypeName,
            Value: response.data
        }
    };

    //gets list of user group names for permission page
    async loadUserGroupTypes(): Promise<GroupTypeDTO[]> {
        let response = await this.apiService.get<GroupTypeDTO[]>("/api/v1/GetGroupTypes/");
        if (response.status_code < 200 || response.status_code >= 300) {
            console.error("Failed to get user group names for permission page.");
            throw response;
        }
        return response.data;
    };

    //submit changes on the permission assignments
    async sendUpdatedGroupAssignment(list: UpdatePermissionGroupDTO[], loginID: number, ModuleID: number): Promise<void> {
        let finalLst: UpdatePermissionGroupDTO[] = [];

        list.forEach(el => {
            finalLst.push({
                BaseGroupType: el.BaseGroupType,
                GroupName: el.GroupName,
                GroupID: el.GroupID,
                GroupLevel: el.GroupLevel,
                GroupSecurityID: (typeof el.GroupSecurityID != "undefined") ? el.GroupSecurityID : "",
                ModuleID: ModuleID,
                Empty: el.Empty,
                Default: el.Default
            })
        });

        let response = await this.apiService.post<void>("/api/v1/UpdateUserPermissionEmpGroup/" + loginID, JSON.stringify(finalLst));
        if (response.status_code < 200 || response.status_code >= 300) {
            console.error("Failed to update permission assignments by loginID.");
            throw response;
        }
        return response.data;
    };

    //get the packages available for the passed loginID
    async getUserPackages(loginID: number): Promise<SalesPackageAssignmentWSTO[]> {
        let response = await this.apiService.get<SalesPackageAssignmentWSTO[]>("/api/v1/GetUserPackages/" + loginID);
        if (response.status_code < 200 || response.status_code >= 300) {
            console.error("Failed to get user packages by loginID.");
            throw response;
        }
        return response.data;
    };

    //returns gem history for passed loginID
    async getGemHistory(loginID: number): Promise<GemHistoryDTOV2[]> {
        let response = await this.apiService.get<GemHistoryDTOV2[]>("/api/v1/GetGemHistory/" + loginID);
        if (response.status_code < 200 || response.status_code >= 300) {
            console.error("Failed to get user gem history by loginID.");
            throw response;
        }
        return response.data;
    };

    //returns currency history for passed loginID
    async getCurrencyHistory(loginID: number): Promise<SpendingPointHistoryDTOV2[]> {
        let response = await this.apiService.get<SpendingPointHistoryDTOV2[]>("/api/v1/GetCurrencyHistory/" + loginID);
        if (response.status_code < 200 || response.status_code >= 300) {
            console.error("Failed to get user emp group info by loginID.");
            throw response;
        }
        return response.data;
    };

    //returns Win or Loss data of selected user
    async getChallengeIWonData(loginID: number): Promise<number> {
        let response = await this.apiService.get<number>("/api/v1/GetChallengeIWonData/" + loginID);
        if (response.status_code < 200 || response.status_code >= 300) {
            console.error("Failed to get user emp win/loss data by loginID.");
            throw response;
        }
        return response.data;
    };

    //returns categories for logged in company for selected user
    async getCategories(): Promise<AttributeCategoryDTO[]> {
        let response = await this.apiService.get<AttributeCategoryDTO[]>("/api/v1/GetCategories/");
        if (response.status_code < 200 || response.status_code >= 300) {
            console.error("Failed to get categories for company.");
            throw response;
        }
        return response.data;
    };

    //returns list of avialable languages
    async getLanguages(): Promise<Language[]> {
        let response = await this.apiService.get<Language[]>("/api/v1/GetLanguages/");
        if (response.status_code < 200 || response.status_code >= 300) {
            console.error("Failed to get available languages.");
            throw response;
        }
        return response.data;
    };

    //given a new login object, updates that specified user
    async commitUserChanges(loginObj: UpdateUserDTO): Promise<string> {
        let response = await this.apiService.post<string>("/api/v1/CommitUserChanges/", JSON.stringify(loginObj));
        if (response.status_code < 200 || response.status_code >= 300) {
            console.error("Failed to update user by loginObj.");
            throw response;
        }
        return response.data;
    };

    //given a new login object, updates that specified user
    async commitNewUser(newData: UpdateUserDTO): Promise<number> {
        let response = await this.apiService.post<string>("/api/v1/AddUser/", JSON.stringify(newData));
        if (response.status_code < 200 || response.status_code >= 300) {
            console.error("Failed to update user by newData.");
            throw response;
        }
        return Number(response.data) || -1;
    };

    //resets password for passed loginID
    async resetPassword(loginID: number): Promise<boolean> {
        let response = await this.apiService.get<boolean>("/api/v1/ResetPassword/" + loginID);
        if (response.status_code < 200 || response.status_code >= 300) {
            console.error("Failed to reset password by loginID.");
            throw response;
        }
        return response.data;
    };

    //unlocks the users account
    async unlockAccount(loginID: number): Promise<boolean> {
        let response = await this.apiService.post<boolean>("/api/v1/unlockaccount/" + loginID);
        if (response.status_code < 200 || response.status_code >= 300) {
            console.error("Failed to unlock account by loginID.");
            throw response;
        }
        return response.data;
    };

    //loads user's dashboards
    async loadDashboards(loginID: number): Promise<DashboardDTO[]> {
        let response = await this.apiService.get<DashboardDTO[]>("/api/v2/dashboards/owned?loginId=" + loginID);
        if (response.status_code < 200 || response.status_code >= 300) {
            console.error("Failed to get dashboards info by loginID.");
            throw response;
        }
        return response.data;
    };

    //changes dashboard's owner
    async changeDashboardOwnership(dashboardIDs: number[], oldLoginID: number, newLoginID: number): Promise<void> {
        let response = await this.apiService.put<void>("/api/v2/dashboardOwnership/" + oldLoginID + "/" + newLoginID, dashboardIDs);
        if (response.status_code < 200 || response.status_code >= 300) {
            console.error("Failed to change dashboard owner by loginIDs.");
            throw response;
        }
        return response.data;
    };

    // Approves a pending profile picture request
    async approveProfilePicture (loginId) {
        let response = await this.apiService.put<void>('/api/v1/profilePictureApprove/' + loginId);

        return response;
    };
    // Denies a pending profile picture request
    async denyProfilePicture (loginId) {
        let response = await this.apiService.put<void>('/api/v1/profilePictureDeny/' + loginId);

        return response;
    };

    async getPageUserAccess() {
        let response = await this.apiService.get<any>(`/api/v1/getPageAccessForUser/UserProfileManagement`);
        return response.data;
    };


    async loadPagesAndChildPages(): Promise<pagePackage[]> {
        let response = await this.apiService.get<pagePackage[]>("/api/v1/pagesAndChildPages");
        if(response.status_code < 200 || response.status_code >= 300){
            console.error("Failed to load page groups");
            throw response;
        }
        return response.data;
    }

    async loadPageAccessByLogin(loginID: number): Promise<PageDTO[]> {
        let response = await this.apiService.put<PageDTO[]>('/api/v1/pageAccessByLogin', loginID);
        if(response.status_code < 200 || response.status_code >= 300){
            console.error("Failed to load page access by Login ID");
            throw response;
        }
        return response.data;
    }

    async loadPageAccess(profile: ProfileDTO): Promise<PageDTO[]> {
        let response = await this.apiService.put<PageDTO[]>('/api/v1/pageAccess', profile);
        if(response.status_code < 200 || response.status_code >= 300){
            console.error("Failed to load page access by ProfileID");
            throw response;
        }
        return response.data;
    }

    async loadPageAccessForUser(access: pageAccessForUser): Promise<PageDTO[]> {
        let response = await this.apiService.put<PageDTO[]>('/api/v1/pageAccessForUser', access);
        if(response.status_code < 200 || response.status_code >= 300){
            console.error("Failed to load page acess by Login ID and Profile ID");
            throw response;
        }
        return response.data;
    }

    async updatePageAccess(parameters: AddEditPageAccessByLoginParameters): Promise<void>{
        let response = await this.apiService.post<void>("/api/v1/pageAccessByLogin", parameters);
        if (response.status_code < 200 || response.status_code >= 300) {
            console.error("Failed to edit page access");
            throw response;
        }
        return response.data;
    }

    async getLoggedInProfile(): Promise<ProfileDTO>{
        let response = await this.apiService.get<ProfileDTO>("/api/v1/getLoggedInProfile/");
        if (response.status_code < 200 || response.status_code >= 300) {
            console.error("Failed to load profile for logged in user");
            throw response;
        }
        return response.data;
    }

    async getPageAccessForLoggedInUser(profile: ProfileDTO): Promise<PageDTO[]>{
        let response = await this.apiService.put<PageDTO[]>("/api/v1/pageAccessForLoggedInUser/", profile);
        if (response.status_code < 200 || response.status_code >= 300) {
            console.error("Failed to load page access for user");
            throw response;
        }
        return response.data;
    }

    async loadResponsiveDashboardModules(): Promise<any> {
        let response = await this.apiService.get<any>("/api/v1/dashboardModulesResponsive");
        if(response.status_code < 200 || response.status_code >= 300){
            console.error("Failed to load responsive dashboard modules");
            throw response;
        }
        return response.data;
    }

    async loadDashboardModulesAccess(parameters: GetModuleAccessByLoginIDParameters): Promise<any> {
        let response = await this.apiService.put<any>("/api/v1/moduleAccessByLoginID", parameters);
        if(response.status_code < 200 || response.status_code >= 300){
            console.error("Failed to load dashboard modules access");
            throw response;
        }
        return response.data;
    }

    async updateModuleAccessByLoginID(params: AddEditModuleAccessByLoginParameters): Promise<void>{
        let response = await this.apiService.post<void>("/api/v1/moduleAccessByLoginID", params);
        if (response.status_code < 200 || response.status_code >= 300) {
            console.error("Failed to edit moduleAccesses");
            throw response;
        }
        return response.data;
    }

    async loadSalesPackages(): Promise<any> {
        let response = await this.apiService.get<any>("/api/v1/packages");
        if(response.status_code < 200 || response.status_code >= 300){
            console.error("Failed to load SalesPackages");
            throw response;
        }
        return response.data;
    }

    async removePageAccessByLoginID(loginID: number): Promise<any> {
        let response = await this.apiService.post<any>("/api/v1/removePageAccessByLogin", loginID);
        if(response.status_code < 200 || response.status_code >= 300){
            console.error("Failed to remove page access by loginID");
            throw response;
        }
        return response.data;
    }

    async removeModuleAccessByLoginID(loginID: number): Promise<void>{
        let response = await this.apiService.post<void>("/api/v1/removeModuleAccessByLoginID", loginID);
        if (response.status_code < 200 || response.status_code >= 300) {
            console.error("Failed to remove moduleAccesses");
            throw response;
        }
        return response.data;
    }
}
